import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { StatusDot } from './schoolEquipmentChrome'

const getSeverityConfig = (t) => ({
  critical: {
    className: 'bg-critical/20 text-critical',
    label: t('alerts.severityCritical'),
  },
  warning: {
    className: 'bg-warning/15 text-warning',
    label: t('alerts.severityWarning'),
  },
  high: {
    className: 'bg-warning/15 text-warning',
    label: t('alerts.severityHigh'),
  },
  major: {
    className: 'bg-warning/15 text-warning',
    label: t('alerts.severityMajor'),
  },
  info: {
    className: 'bg-accent-cyan/20 text-accent-cyan',
    label: t('alerts.severityInfo'),
  },
  minor: {
    className: 'bg-accent-cyan/20 text-accent-cyan',
    label: t('alerts.severityMinor'),
  },
  low: {
    className: 'bg-muted-500/20 text-muted-200',
    label: t('alerts.severityLow'),
  },
})

const normalizeStatus = (value = '') => String(value).toLowerCase()

const getSeverityMeta = (value = '', t) => {
  const severityConfig = getSeverityConfig(t)
  const key = String(value).toLowerCase()
  const mapped = severityConfig[key]
  const tone =
    key === 'critical'
      ? 'critical'
      : key === 'warning' || key === 'high' || key === 'major'
        ? 'warning'
        : 'muted'
  return {
    tone,
    label: mapped?.label || value || '—',
  }
}

const formatTimestamp = (value) => {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString('ru-KZ', {
      timeZone: 'Asia/Almaty',
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: '2-digit',
    })
  } catch (error) {
    return value
  }
}

const stripSchoolFromSummary = (text, schoolName) => {
  if (!text || !schoolName) return text
  const escaped = String(schoolName).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return String(text).replace(new RegExp(`\\s*\\(${escaped}\\)\\s*$`), '').trim()
}

const resolvedStates = new Set(['resolved', 'inactive'])

const AlertsTable = ({
  alerts,
  isLoading,
  onAcknowledge,
  acknowledgeLoading,
  page = 1,
  totalPages = 1,
  pageSize = 5,
  onPageChange,
  onChangePageSize,
  totalCount,
  schoolName,
}) => {
  const { t } = useTranslation();
  const [pendingAck, setPendingAck] = useState(null)
  const [sortField, setSortField] = useState('updatedAt')
  const [sortDirection, setSortDirection] = useState('desc')

  const canAcknowledge = typeof onAcknowledge === 'function'

  // Функция сортировки алертов
  const sortAlerts = (alerts, field, direction) => {
    if (!Array.isArray(alerts)) return alerts

    return [...alerts].sort((a, b) => {
      let aValue, bValue

      switch (field) {
        case 'alertName':
          aValue = a.labels?.alertname || a.annotations?.summary || ''
          bValue = b.labels?.alertname || b.annotations?.summary || ''
          break
        case 'severity':
          aValue = a.labels?.severity || ''
          bValue = b.labels?.severity || ''
          // Порядок приоритета для severity
          const severityOrder = { critical: 0, warning: 1, high: 2, major: 3, info: 4, minor: 5, low: 6 }
          aValue = severityOrder[aValue.toLowerCase()] ?? 999
          bValue = severityOrder[bValue.toLowerCase()] ?? 999
          break
        case 'status':
          aValue = a.status || ''
          bValue = b.status || ''
          // Порядок приоритета для status
          const statusOrder = { firing: 0, active: 1, pending: 2, suppressed: 3, resolved: 4, inactive: 5 }
          aValue = statusOrder[aValue.toLowerCase()] ?? 999
          bValue = statusOrder[bValue.toLowerCase()] ?? 999
          break
        case 'startsAt':
          aValue = new Date(a.startsAt || 0).getTime()
          bValue = new Date(b.startsAt || 0).getTime()
          break
        case 'updatedAt':
          aValue = new Date(a.updatedAt || 0).getTime()
          bValue = new Date(b.updatedAt || 0).getTime()
          break
        case 'summary':
          aValue = a.annotations?.summary || a.annotations?.description || ''
          bValue = b.annotations?.summary || b.annotations?.description || ''
          break
        default:
          return 0
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase()
        bValue = bValue.toLowerCase()
      }

      if (aValue < bValue) return direction === 'asc' ? -1 : 1
      if (aValue > bValue) return direction === 'asc' ? 1 : -1
      return 0
    })
  }

  // Обработчик клика по заголовку колонки
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  // Отсортированные алерты
  const sortedAlerts = useMemo(() => {
    return sortAlerts(alerts, sortField, sortDirection)
  }, [alerts, sortField, sortDirection])

  const handleAcknowledge = async (alert) => {
    if (!canAcknowledge) {
      return
    }

    const alertName = alert.annotations?.summary || alert.labels?.alertname || t('alerts.alert')

    if (typeof window !== 'undefined' && window.confirm) {
      const confirmed = window.confirm(t('alerts.confirmAlert', { name: alertName }))
      if (!confirmed) {
        return
      }
    }

    setPendingAck(alert.fingerprint)

    try {
      await onAcknowledge(alert.fingerprint, {
        comment: `Подтверждено оператором (${alert.labels?.alertname || alert.fingerprint})`,
      })
    } catch (error) {
      if (typeof window !== 'undefined' && window.alert) {
        window.alert(t('alerts.failedToAcknowledge'))
      }
    } finally {
      setPendingAck(null)
    }
  }

  const isActionDisabled = (state) => resolvedStates.has(state) || state === 'suppressed'

  return (
    <section className="rounded-3xl bg-surface-1 p-6">
      <header className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h3 className="font-display text-xl text-white md:text-2xl">{t('alerts.title')}</h3>
          <p className="mt-1 text-sm text-muted-400">{t('alerts.subtitle')}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-500">
          <span className="inline-flex items-center gap-1.5">
            <span className="material-icons text-base text-muted-500">notifications</span>
            {t('alerts.events', { count: typeof totalCount === 'number' ? totalCount : (alerts?.length || 0) })}
          </span>
          {(sortField !== 'updatedAt' || sortDirection !== 'desc') && (
            <button
              onClick={() => {
                setSortField('updatedAt')
                setSortDirection('desc')
              }}
              className="inline-flex items-center gap-1.5 text-muted-500 transition hover:text-white"
            >
              <span className="material-icons text-sm">sort</span>
              {t('alerts.resetSort')}
            </button>
          )}
          {typeof onChangePageSize === 'function' && (
            <label className="inline-flex items-center gap-2">
              <span>{t('alerts.show')}</span>
              <select
                value={pageSize}
                onChange={(event) => onChangePageSize(Number(event.target.value))}
                className="rounded-lg bg-white/[0.04] px-2 py-1 text-xs text-muted-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
              >
                {[5, 10, 20].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      </header>

      <div className="overflow-hidden rounded-3xl bg-surface-2">
        <div className="grid grid-cols-12 gap-3 border-b border-white/[0.04] px-4 py-3 text-[11px] font-medium uppercase tracking-[0.08em] text-muted-500">
          <button
            onClick={() => handleSort('alertName')}
            className={`col-span-5 flex items-center gap-1 text-left transition hover:text-white ${
              sortField === 'alertName' ? 'text-white' : ''
            }`}
          >
            {t('alerts.alert')}
            <span className="material-icons text-sm">
              {sortField === 'alertName' ? (sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more'}
            </span>
          </button>
          <button
            onClick={() => handleSort('severity')}
            className={`col-span-3 flex items-center gap-1 text-left transition hover:text-white ${
              sortField === 'severity' ? 'text-white' : ''
            }`}
          >
            {t('alerts.level')}
            <span className="material-icons text-sm">
              {sortField === 'severity' ? (sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more'}
            </span>
          </button>
          <button
            onClick={() => handleSort('updatedAt')}
            className={`col-span-3 flex items-center gap-1 text-left transition hover:text-white ${
              sortField === 'updatedAt' ? 'text-white' : ''
            }`}
          >
            {t('alerts.lastUpdate')}
            <span className="material-icons text-sm">
              {sortField === 'updatedAt' ? (sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward') : 'unfold_more'}
            </span>
          </button>
          <div className="col-span-1" />
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center px-6 py-10 text-sm text-muted-300">
            <span className="material-icons animate-spin text-muted-400">progress_activity</span>
            <span className="ml-3">{t('alerts.updatingAlerts')}</span>
          </div>
        ) : sortedAlerts?.length ? (
          <div className="divide-y divide-white/[0.04]">
            {sortedAlerts.map((alert) => {
              const state = normalizeStatus(alert.status)
              const alertName = alert.labels?.alertname || t('alerts.noName')
              const severity = alert.labels?.severity || 'n/a'
              const severityMeta = getSeverityMeta(severity, t)
              const generator = alert.generatorURL
              const instance = alert.labels?.instance || alert.labels?.job || '—'

              const summary = stripSchoolFromSummary(alert.annotations?.summary || alertName, schoolName)

              return (
                <article key={alert.fingerprint} className="group grid grid-cols-12 items-center gap-3 px-4 py-4 text-sm text-muted-200 transition hover:bg-white/[0.03]">
                  <div className="col-span-5 flex min-w-0 flex-col gap-1">
                    <span className="font-semibold text-white">{summary}</span>
                    <span className="inline-flex min-w-0 items-center gap-1 font-mono text-[11px] tabular-nums text-muted-500">
                      <span className="truncate">{instance}</span>
                      {generator ? (
                        <a
                          href={generator}
                          target="_blank"
                          rel="noreferrer"
                          title={t('alerts.openSource')}
                          className="inline-flex shrink-0 text-muted-500 transition hover:text-white"
                        >
                          <span className="material-icons text-[14px]">open_in_new</span>
                        </a>
                      ) : null}
                    </span>
                  </div>
                  <div className="col-span-3">
                    <StatusDot tone={severityMeta.tone} label={severityMeta.label} />
                  </div>
                  <div className="col-span-3 text-xs text-muted-400">
                    <div>{t('alerts.updated')} {formatTimestamp(alert.updatedAt)}</div>
                    <div>{t('alerts.start')} {formatTimestamp(alert.startsAt)}</div>
                  </div>
                  <div className="col-span-1 flex items-center justify-end">
                    {canAcknowledge && !isActionDisabled(state) ? (
                      <button
                        type="button"
                        onClick={() => handleAcknowledge(alert)}
                        disabled={acknowledgeLoading || pendingAck === alert.fingerprint}
                        className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-medium text-muted-400 opacity-100 transition hover:bg-white/[0.06] hover:text-white disabled:cursor-not-allowed disabled:opacity-50 md:opacity-0 md:group-hover:opacity-100"
                      >
                        <span className="material-icons text-sm">
                          {acknowledgeLoading && pendingAck === alert.fingerprint ? 'hourglass_top' : 'done_all'}
                        </span>
                        Ack
                      </button>
                    ) : null}
                  </div>
                </article>
              )
            })}
          </div>
        ) : (
          <div className="px-6 py-10 text-center text-sm text-muted-300">{t('alerts.noActiveAlerts')}</div>
        )}
      </div>
      {totalPages > 1 && typeof onPageChange === 'function' && (
        <footer className="mt-4 flex items-center justify-between text-xs text-muted-400">
          <span>
            {t('alerts.pageOf', { current: page, total: totalPages })}
          </span>
          <div className="inline-flex items-center gap-1 rounded-xl bg-white/[0.04] p-1">
            <button
              type="button"
              onClick={() => onPageChange(Math.max(1, page - 1))}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-300 transition hover:bg-white/[0.08] hover:text-white disabled:opacity-40"
              disabled={page === 1}
            >
              <span className="material-icons text-sm">chevron_left</span>
            </button>
            <button
              type="button"
              onClick={() => onPageChange(Math.min(totalPages, page + 1))}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-300 transition hover:bg-white/[0.08] hover:text-white disabled:opacity-40"
              disabled={page === totalPages}
            >
              <span className="material-icons text-sm">chevron_right</span>
            </button>
          </div>
        </footer>
      )}
    </section>
  )
}

export default AlertsTable


